import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import CustomButton from '../Button/CustomButton';
import { isEmpty } from '../../utils/lodash';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { addPaymentMethod, createPaymentIntent } from '../../redux/payment/action';
import { SITE_URL } from '../../configurations/api/url';

type FormData = {
    name: string;
    isCardComplete: boolean;
};

export interface IPaymentMethod {
    onClose: () => void;
}

const PaymentMethod: React.FC<IPaymentMethod> = ({ onClose }) => {
    const [loading, setIsLoading] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const { themeStyle } = useThemeContext();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        isCardComplete: false,
    });
    const dispatchPromise = useDispatchPromise();

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleCardChange = (event: StripePaymentElementChangeEvent) => {
        setFormData(prevState => ({
            ...prevState,
            isCardComplete: event.complete,
        }));
    };

    const handleAddPaymentMethod = async (): Promise<boolean> => {
        if (!stripe || !elements) return false;

        try {
            const secret: string = (await dispatchPromise(
                createPaymentIntent({
                    currency: 'usd',
                    payment_method_type: 'card',
                    amount: 100,
                }),
            )) as string;

            const { error: submitError } = await elements.submit();
            if (submitError) {
                showToastMessage(submitError.message, 'error');
                return false;
            }

            // Create the payment method
            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: SITE_URL,
                    payment_method_data: {
                        billing_details: {
                            name: formData.name,
                        },
                    },
                },
                clientSecret: secret,
                redirect: 'if_required',
            });

            if (error) {
                showToastMessage(error.message, 'error');
                return false;
            }

            if (setupIntent && setupIntent.payment_method) {
                console.log('setupIntent', setupIntent);
                // Pass the payment method ID to addPaymentMethod
                await dispatchPromise(
                    addPaymentMethod({
                        payment_method: setupIntent.payment_method as string,
                        fullName: formData.name,
                        intent: setupIntent,
                    }),
                );
                showToastMessage('Payment method added successfully', 'success');
                return true;
            } else {
                showToastMessage('No payment method created', 'error');
                return false;
            }
        } catch (error: any) {
            showToastMessage(error.message || 'Failed to add payment method', 'error');
            return false;
        }
    };

    const handleOnSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setIsLoading(true);

        try {
            const success = await handleAddPaymentMethod();
            if (success) {
                setIsLoading(false);
                onClose && onClose();
            } else {
                setIsLoading(false);
            }
        } catch (error: any) {
            console.error('Payment error:', error);
            showToastMessage(error.message || 'An error occurred', 'error');
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: '500px',
                margin: '0 auto',
                padding: 3,
                borderRadius: 1,
                boxShadow:
                    '0px 1px 1px 0px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px rgba(103, 110, 118, 0.16), 0px 2px 5px 0px rgba(103, 110, 118, 0.08)',
            }}>
            <Typography
                style={{ color: theme[themeStyle].tag_white, fontSize: 16, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                mb={2}>
                Add Payment Method
            </Typography>

            <Typography
                style={{ color: theme[themeStyle].tag_white, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                mb={3}>
                Add a new payment method to your account.
            </Typography>

            <Box component="form" onSubmit={handleOnSubmit} noValidate>
                <TextField
                    label="Name on Card"
                    name="name"
                    fullWidth
                    variant="outlined"
                    size="small"
                    margin="normal"
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 16,
                            '& fieldset': {
                                borderColor: theme[themeStyle].card_grey,
                            },
                            '&:hover fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: theme[themeStyle].primary_white,
                        },
                        '& .MuiInputLabel-root': {
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: theme[themeStyle].highlight,
                        },
                    }}
                    value={formData.name}
                    onChange={handleInputChange}
                />
                <Box sx={{ mb: 3 }}>
                    <PaymentElement onChange={handleCardChange} />
                </Box>

                <CustomButton
                    label="Add Payment Method"
                    sx={{ width: '100%', mt: 2 }}
                    loading={loading}
                    customStyle={{
                        height: '50px',
                    }}
                    onClick={handleOnSubmit}
                    disabled={isEmpty(formData.name) || !stripe || !elements || !formData.isCardComplete}
                />
            </Box>
        </Box>
    );
};

export default PaymentMethod;
